import { useState, useEffect, useRef, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/layout/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { CompletedActivity } from '@/types';
import { capitalizeTitle } from '@/adapters/calendarAdapter';
import { Footprints, Bike, Waves, Heart, Bot, ChevronDown, ChevronUp, Zap, MessageSquare } from 'lucide-react';
import { ActivityExpandedContent } from './ActivityExpandedContent';
import { GarminAttribution } from './GarminAttribution';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useNavigate } from 'react-router-dom';
import { fetchTrainingLoad } from '@/lib/api';
import { enrichActivitiesWithTss } from '@/lib/tss-utils';
import { useQuery } from '@tanstack/react-query';
import { MetricInfoButton } from '@/components/MetricInfoButton';
import { log } from '@/lib/logger';

interface ActivityListProps {
  activities: CompletedActivity[];
  initialExpandedId?: string | null;
}

const sportIcons = {
  running: Footprints,
  cycling: Bike,
  swimming: Waves,
  triathlon: Footprints,
};

/**
 * Build a draft message summarising a completed activity for coach feedback.
 * Includes activity ID (for backend planned-vs-actual gap analysis lookup),
 * date, activity type, duration, distance, avg pace, avg HR, and TSS
 * when available.
 */
function buildFeedbackDraft(activity: CompletedActivity, distanceLabel: string): string {
  const parts: string[] = [];

  // Activity ID for planned-vs-actual gap analysis lookup
  parts.push(`Activity ID: ${activity.id}`);

  // Date
  try {
    parts.push(`Date: ${format(parseISO(activity.date), 'EEE, MMM d yyyy')}`);
  } catch {
    parts.push(`Date: ${activity.date}`);
  }

  parts.push(`Activity: ${activity.sport}`);
  if (activity.title) parts.push(`Title: ${activity.title}`);
  parts.push(`Duration: ${activity.duration} min`);
  parts.push(`Distance: ${distanceLabel}`);
  if (activity.avgPace) parts.push(`Avg Pace: ${activity.avgPace}`);
  if (activity.avgHeartRate) parts.push(`Avg HR: ${Math.round(activity.avgHeartRate)} bpm`);
  if (activity.trainingLoad > 0) parts.push(`TSS: ${Math.round(activity.trainingLoad)}`);

  return `Give me coaching feedback on this completed session:\n${parts.join('\n')}`;
}

export function ActivityList({ activities, initialExpandedId = null }: ActivityListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId);
  const expandedRef = useRef<HTMLDivElement>(null);
  const { convertDistance, convertElevation } = useUnitSystem();
  const navigate = useNavigate();
  
  // Fetch training load data to enrich activities with TSS
  const { data: trainingLoadData } = useQuery({
    queryKey: ['trainingLoad', 60],
    queryFn: () => {
      log.debug('ACTIVITIES', 'Fetching training load for 60 days');
      return fetchTrainingLoad(60);
    },
    retry: (failureCount, error) => {
      // Don't retry on timeout errors or 500 errors (fetchTrainingLoad returns empty response for 500s)
      if (error && typeof error === 'object') {
        const apiError = error as { code?: string; message?: string; status?: number };
        if (apiError.status === 500 || apiError.status === 503 ||
            apiError.code === 'ECONNABORTED' || 
            (apiError.message && apiError.message.includes('timed out'))) {
          return false;
        }
      }
      return failureCount < 1;
    },
    staleTime: 0, // Always refetch - training load changes frequently
    refetchOnMount: true, // Force fresh data on page load
    refetchOnWindowFocus: true, // Refetch when window regains focus
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
  });
  
  // Enrich activities with TSS from training load endpoint
  const enrichedActivities = useMemo(() => {
    // Defensive: ensure activities is always an array
    let activitiesArray: typeof activities = [];
    if (Array.isArray(activities)) {
      activitiesArray = activities;
    } else if (activities && typeof activities === 'object' && 'activities' in activities) {
      // Handle case where API returns { activities: [...] }
      const nested = (activities as { activities?: unknown }).activities;
      activitiesArray = Array.isArray(nested) ? nested : [];
    }

    // Dedup: by id first, then by (date + duration + distance) fingerprint
    const seenIds = new Set<string>();
    const seenFingerprints = new Set<string>();
    activitiesArray = activitiesArray.filter((a) => {
      if (a.id && seenIds.has(a.id)) return false;
      if (a.id) seenIds.add(a.id);
      // Secondary fingerprint for activities with duplicate/missing IDs from same sync
      const date = a.date?.substring(0, 10) ?? '';
      const dur = Math.round(a.duration ?? 0);
      const dist = Math.round((a.distance ?? 0) * 10) / 10;
      const fp = `${date}|${dur}|${dist}|${a.sport ?? ''}`;
      if (seenFingerprints.has(fp)) return false;
      seenFingerprints.add(fp);
      return true;
    });

    const enriched = enrichActivitiesWithTss(activitiesArray, trainingLoadData);
    // Ensure we always return an array
    return Array.isArray(enriched) ? enriched : [];
  }, [activities, trainingLoadData]);
  
  // Expand the activity if initialExpandedId is provided
  useEffect(() => {
    if (initialExpandedId && Array.isArray(activities) && activities.length > 0) {
      setExpandedId(initialExpandedId);
      // Scroll to the expanded activity after a delay to ensure it's rendered
      setTimeout(() => {
        if (expandedRef.current) {
          expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [initialExpandedId, activities]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter out invalid/empty activities
  const validActivities = (Array.isArray(enrichedActivities) ? enrichedActivities : []).filter((activity) => {
    if (!activity || !activity.id) {
      return false;
    }

    // Must have required fields
    if (!activity.date || !activity.sport) {
      return false;
    }

    // Filter out placeholder/mock activities
    const title = (activity.title || '').toLowerCase();
    if (title.includes('placeholder') || title.includes('mock') || title.includes('sample') || title === 'untitled activity') {
      return false;
    }

    // Filter out planned-only entries: no actual execution data (duration = 0)
    if ((activity.duration ?? 0) === 0) {
      return false;
    }

    return true;
  });

  if (validActivities.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground bg-transparent rounded-lg border border-border/50 backdrop-blur-sm">
        <p>No activities found</p>
        <p className="text-xs mt-2">Connect Strava, Garmin, or Apple Health (via HealthKit) to sync activities</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 bg-transparent">
      {validActivities.map((activity) => {
          const Icon = sportIcons[activity.sport] || Footprints;
          const isExpanded = expandedId === activity.id;
          
          const dateStr = activity.date ? (() => {
            try {
              return format(parseISO(activity.date), 'EEE, MMM d yyyy');
            } catch {
              return activity.date;
            }
          })() : 'Unknown';

          return (
            <div
              key={activity.id}
              ref={isExpanded && initialExpandedId === activity.id ? expandedRef : null}
            >
              <Collapsible
                open={isExpanded}
                onOpenChange={() => toggleExpand(activity.id)}
              >
                <Card 
                  className={cn(
                    'transition-all bg-muted/40 border-border/50 shadow-none backdrop-blur-sm',
                    isExpanded && 'ring-2 ring-primary/50 bg-muted/50'
                  )}
                >
                <CollapsibleTrigger asChild>
                  <CardContent className="p-4 cursor-pointer bg-transparent hover:bg-muted/30 transition-colors">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-muted/50 rounded-lg">
                        <Icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground truncate">
                          {capitalizeTitle(activity.title || 'Untitled Activity')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {dateStr}
                        </p>
                        {activity.source === 'garmin' && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <img
                              src="/garmin-tag-white.png"
                              alt="Garmin"
                              className="h-3 object-contain opacity-70"
                            />
                            <GarminAttribution deviceModel={activity.deviceModel} />
                          </div>
                        )}
                        {activity.source === 'apple_health' && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <svg viewBox="0 0 24 24" fill="#FF2D55" className="h-3 w-3 opacity-70" aria-hidden>
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                            <span className="text-[10px] text-muted-foreground leading-tight">
                              {activity.sourceApp ? `${activity.sourceApp} via Apple Health` : 'Apple Health'}
                            </span>
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="shrink-0">
                        {activity.sport}
                      </Badge>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {/* Metrics row */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                      <span className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground">{activity.duration || 0}</span>
                        <span className="text-muted-foreground">min</span>
                      </span>
                      <div className="w-px h-4 bg-border" />
                      <span className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground">
                          {convertDistance(activity.distance || 0).value.toFixed(1)}
                        </span>
                        <span className="text-muted-foreground">
                          {convertDistance(activity.distance || 0).unit}
                        </span>
                      </span>
                      {activity.trainingLoad > 0 && (
                        <>
                          <div className="w-px h-4 bg-border" />
                          <span className="flex items-center gap-1.5">
                            <span className="font-medium text-foreground">{Math.round(activity.trainingLoad)}</span>
                            <span className="text-muted-foreground">TSS</span>
                            <MetricInfoButton metricKey="tss" />
                          </span>
                        </>
                      )}
                      {activity.avgHeartRate && (
                        <>
                          <div className="w-px h-4 bg-border" />
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Heart className="h-4 w-4" />
                            <span>{Math.round(activity.avgHeartRate)} bpm</span>
                          </span>
                        </>
                      )}
                      {activity.sport === 'cycling' && activity.normalizedPower !== undefined && activity.normalizedPower !== null && (
                        <>
                          <div className="w-px h-4 bg-border" />
                          <span className="flex items-center gap-1 text-muted-foreground text-sm">
                            <Zap className="h-3.5 w-3.5 text-[#00C8FF]" />
                            <span className="font-medium text-foreground">{Math.round(activity.normalizedPower)}</span>
                            <span>w NP</span>
                            <MetricInfoButton metricKey="normalizedPower" />
                          </span>
                        </>
                      )}
                      {activity.intensityFactor !== undefined && activity.intensityFactor !== null && (
                        <>
                          <div className="w-px h-4 bg-border" />
                          <span className={cn(
                            "text-sm flex items-center gap-1",
                            activity.intensityFactor >= 1.0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"
                          )}>
                            IF {activity.intensityFactor.toFixed(2)}
                            <MetricInfoButton metricKey="intensityFactor" />
                          </span>
                        </>
                      )}
                    </div>

                    {/* Get Coach Feedback button */}
                    {!isExpanded && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dist = convertDistance(activity.distance || 0);
                            const distanceLabel = `${dist.value.toFixed(1)} ${dist.unit}`;
                            navigate('/coach', {
                              state: { draft_message: buildFeedbackDraft(activity, distanceLabel) },
                            });
                          }}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          Get Coach Feedback
                        </Button>
                      </div>
                    )}

                    {/* Coach Insight Preview */}
                    {activity.coachFeedback && !isExpanded && (
                      <div className="mt-3 p-3 bg-muted/30 rounded-lg border-l-2 border-primary/40 backdrop-blur-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Bot className="h-4 w-4 text-primary" />
                          <span className="text-xs font-medium text-primary">Analysis</span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {activity.coachFeedback}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleTrigger>

              <CollapsibleContent>
                <ActivityExpandedContent
                  activity={activity}
                  onDeleted={() => setExpandedId(null)}
                />
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
        );
      })}
    </div>
  );
}
